/**
 * Gradient step: the colours the blend runs through, the direction and what
 * one blend spans.
 *
 * The uploaded shape's paints (the SVG's fills - each gradient definition,
 * each solid colour - or the raster shape as a whole) are listed. The palette
 * colours are picked in order, either once for every paint of the shape or,
 * per paint, for the selected one; a paint without colours of its own follows
 * the first paint. The shape is filled with a halftone of the blends.
 * Finalizing fixes everything, because the separation and the marking file
 * are computed from it.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack, Typography, Box, Alert, Chip, TextField, MenuItem, Tooltip, IconButton,
  CircularProgress, Slider, Checkbox, FormControlLabel, ButtonBase,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import FinalizeDialog from "../../components/FinalizeDialog";
import PaintSwatch from "../../components/PaintSwatch";
import { useReproductionParams } from "../../helper/entityRoutes";
import {
  gradientReproductionSidebar, GRADIENT_DIRECTIONS, GRADIENT_SCOPES,
} from "../../helper/reproductionFlow";

const imagePanelSx = {
  position: "relative", width: "100%", aspectRatio: "1",
  borderRadius: 2, overflow: "hidden", border: "1px solid",
  borderColor: "divider", bgcolor: "grey.50",
};

const PREVIEW_DEBOUNCE_MS = 250;
const DEFAULT_BLEND_CENTER = 0.5;

// Whose colours a multi-paint shape blends through: one sequence for every
// paint, or a sequence per paint. A frontend notion only - the backend
// stores the sequences per paint, and a paint without its own follows the
// first paint, which is what "same" sends for every paint but the first.
export const GRADIENT_COLOR_MODES = [
  { value: "same", label: "Same for every paint" },
  { value: "paint", label: "Per paint" },
];

// The gradient axis as the page words it: where the first colour sits, where
// the last one sits, and whether the sequence is listed top to bottom.
const AXES = {
  horizontal: { start: "left", end: "right", arrow: "left → right", column: false },
  vertical: { start: "top", end: "bottom", arrow: "top → bottom", column: true },
};

// Blend centers are kept per ordered pair of neighbouring colors, so a
// center survives adding, removing or moving other colors as long as the
// two colors stay neighbours in the same order.
const present = (value) => value !== null && value !== undefined;
const pairKey = (fromSampleId, toSampleId) => `${fromSampleId}>${toSampleId}`;

const blendCentersFor = (sampleIds, centerByPair) => sampleIds.slice(0, -1).map(
  (sampleId, position) => centerByPair[pairKey(sampleId, sampleIds[position + 1])] ?? DEFAULT_BLEND_CENTER,
);

const formatPercent = (fraction) => `${Math.round(fraction * 100)} %`;

// A paint as the page keeps it: the backend's description plus the picks.
const paintState = (paint) => {
  const sampleIds = (paint.palette_sample_ids || []).map(String);
  const centerByPair = {};
  (paint.blend_centers || []).forEach((center, position) => {
    if (typeof center === "number" && position + 1 < sampleIds.length) {
      centerByPair[pairKey(sampleIds[position], sampleIds[position + 1])] = center;
    }
  });
  return {
    id: paint.id || null,
    key: paint.key || "shape",
    label: paint.label || "Shape",
    display: paint.display || { kind: "shape" },
    componentCount: paint.component_count ?? null,
    excluded: Boolean(paint.excluded_from_marking),
    sampleIds,
    centerByPair,
  };
};

// The colour mode a stored setup is in: per paint as soon as any paint but
// the first has colours of its own, else one sequence for every paint.
export const colorModeOf = (paints) => (
  paints.length > 1 && paints.slice(1).some((paint) => paint.sampleIds.length > 0) ? "paint" : "same"
);

// The paints as the "same" mode sends them: only the first keeps its picks,
// every other paint follows it.
export const sharedPaints = (paints) => paints.map((paint, index) => (
  index === 0 ? paint : { ...paint, sampleIds: [], centerByPair: {} }
));

// The colours a paint blends with: its own, or the first paint's while it
// has none (mirrors the backend's resolve_gradient_sequences).
const effectiveSampleIds = (paints, index) => (
  paints[index].sampleIds.length || index === 0 ? paints[index].sampleIds : paints[0].sampleIds
);

// Every colour any marked paint blends through, in order of first use.
export const markedSampleIds = (paints) => {
  const ids = [];
  paints.forEach((paint, index) => {
    if (paint.excluded) return;
    effectiveSampleIds(paints, index).forEach((id) => { if (!ids.includes(id)) ids.push(id); });
  });
  return ids;
};

// Whether the setup can be finalized (the backend's gradient_setup_error).
export const setupComplete = (paints) => {
  const marked = paints.filter((paint) => !paint.excluded);
  if (marked.length === 0) return false;
  if (paints.some((paint, index) => !paint.excluded && effectiveSampleIds(paints, index).length === 0)) return false;
  return markedSampleIds(paints).length >= 2;
};

// Step 2 of the gradient-reproduction flow: pick the palette colors the
// blend runs through, in order - once for every paint of the shape or per
// source paint - plus the direction and scope of the blends. The preview on
// the right is rendered by the backend for the current (unsaved) picks; Next
// stores them, makes the reproduction final and moves on to the Marking
// step - the only step open from then on. A pick is the id of the palette
// sample, never its position, so a re-composed palette cannot shift a pick
// onto another color.
export default function ColorReproductionGradient() {
  const navigate = useNavigate();
  const { reproductionId, stepRoute } = useReproductionParams();

  const [palette, setPalette] = useState([]);
  // Columns of the marked palette plate (get_palette "grid"), so the
  // colors sit where they sit on the physical sample.
  const [paletteColumns, setPaletteColumns] = useState(null);
  const [paints, setPaints] = useState([paintState({})]);
  const [selectedPaint, setSelectedPaint] = useState(0);
  const [colorMode, setColorMode] = useState("same");
  const [direction, setDirection] = useState("horizontal");
  const [scope, setScope] = useState("whole");
  const [maxPrimaries, setMaxPrimaries] = useState(8);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [sourceImageUrl, setSourceImageUrl] = useState("");
  const sourceImageUrlRef = useRef("");
  const [previewUrl, setPreviewUrl] = useState("");
  const previewUrlRef = useRef("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewRequestRef = useRef(0);
  const mountedRef = useRef(true);

  const replaceObjectUrl = (ref, setter, blob) => {
    if (ref.current) URL.revokeObjectURL(ref.current);
    const url = blob ? URL.createObjectURL(blob) : "";
    ref.current = url;
    setter(url);
  };

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      try {
        const [settingsResponse, paletteResponse] = await Promise.all([
          axios.post("api/reproduction/gradient/settings", { reproduction_id: reproductionId }),
          axios.post("api/reproduction/get_palette", { reproduction_id: reproductionId }),
        ]);
        if (!mountedRef.current) return;
        if (settingsResponse.data.setup_final) {
          // The colors are final: only the Marking step is open.
          navigate(stepRoute("marking", "GRADIENT_REPRODUCTION"), { replace: true });
          return;
        }
        const setup = settingsResponse.data.setup || {};
        const storedPaints = (setup.paints || []).length
          ? setup.paints
          // A setup without paints (older server): one implicit paint
          // holding the flat sequence.
          : [{ palette_sample_ids: setup.palette_sample_ids, blend_centers: setup.blend_centers }];
        const loadedPaints = storedPaints.map(paintState);
        setPaints(loadedPaints);
        setColorMode(colorModeOf(loadedPaints));
        setSelectedPaint(0);
        setDirection(setup.direction || "horizontal");
        setScope(setup.scope || "whole");
        if (settingsResponse.data.max_primaries) {
          setMaxPrimaries(settingsResponse.data.max_primaries);
        }
        setPalette(paletteResponse.data.palette || []);
        setPaletteColumns(paletteResponse.data.grid?.columns || null);
      } catch (error) {
        console.error("Error loading the gradient settings:", error);
        const detail = error?.response?.data?.error;
        setErrorMessage(detail || "Loading the gradient settings failed.");
      } finally {
        if (mountedRef.current) setLoaded(true);
      }
      try {
        const response = await axios.post(
          "api/reproduction/get_input_image_preview",
          { reproduction_id: reproductionId },
          { responseType: "blob" },
        );
        if (mountedRef.current) replaceObjectUrl(sourceImageUrlRef, setSourceImageUrl, response.data);
      } catch (error) {
        console.error("Error loading the source shape:", error);
      }
    };
    load();
    return () => {
      mountedRef.current = false;
      [sourceImageUrlRef, previewUrlRef].forEach((ref) => {
        if (ref.current) URL.revokeObjectURL(ref.current);
      });
    };
  }, []);

  const multiPaint = paints.length > 1;
  const perPaint = multiPaint && colorMode === "paint";
  // The paints as the preview and the save see them: with one sequence for
  // every paint, only the first paint's picks count.
  const effectivePaints = perPaint ? paints : sharedPaints(paints);
  // The paint the picks below belong to.
  const currentIndex = perPaint ? Math.min(selectedPaint, paints.length - 1) : 0;
  const current = paints[currentIndex];
  const pickedSampleIds = current.sampleIds;
  const blendCenters = blendCentersFor(pickedSampleIds, current.centerByPair);
  const axis = AXES[direction] || AXES.horizontal;

  // What the backend gets: the flat sequence of the first paint (also the
  // whole setup of a single-paint shape) and, when the paints have ids,
  // every paint's sequence and exclusion.
  const setupPayload = () => {
    const first = effectivePaints[0];
    const payload = {
      direction,
      scope,
      palette_sample_ids: first.sampleIds,
      blend_centers: blendCentersFor(first.sampleIds, first.centerByPair),
    };
    const withIds = effectivePaints.filter((paint) => paint.id);
    if (withIds.length) {
      payload.paints = withIds.map((paint) => ({
        id: paint.id,
        palette_sample_ids: paint.sampleIds,
        blend_centers: blendCentersFor(paint.sampleIds, paint.centerByPair),
        excluded_from_marking: paint.excluded,
      }));
    }
    return payload;
  };
  const payloadKey = JSON.stringify(setupPayload());

  // Live preview of the blends for the current picks (debounced; a newer
  // request supersedes an older one still in flight).
  useEffect(() => {
    if (!loaded) return undefined;
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await axios.post(
          "api/reproduction/gradient/preview",
          { reproduction_id: reproductionId, ...setupPayload() },
          { responseType: "blob" },
        );
        if (!mountedRef.current || previewRequestRef.current !== requestId) return;
        replaceObjectUrl(previewUrlRef, setPreviewUrl, response.data);
      } catch (error) {
        console.error("Error rendering the gradient preview:", error);
      } finally {
        if (mountedRef.current && previewRequestRef.current === requestId) setPreviewLoading(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [loaded, payloadKey]);

  const updatePaint = (index, update) => {
    setPaints((all) => all.map((paint, position) => (position === index ? update(paint) : paint)));
  };

  const setBlendCenter = (position, fraction) => {
    const key = pairKey(pickedSampleIds[position], pickedSampleIds[position + 1]);
    updatePaint(currentIndex, (paint) => ({
      ...paint, centerByPair: { ...paint.centerByPair, [key]: fraction },
    }));
  };

  const togglePick = (sampleId) => {
    setErrorMessage(null);
    updatePaint(currentIndex, (paint) => {
      if (paint.sampleIds.includes(sampleId)) {
        return { ...paint, sampleIds: paint.sampleIds.filter((value) => value !== sampleId) };
      }
      if (paint.sampleIds.length >= maxPrimaries) {
        setErrorMessage(`A gradient runs through at most ${maxPrimaries} colors.`);
        return paint;
      }
      return { ...paint, sampleIds: [...paint.sampleIds, sampleId] };
    });
  };

  const removePick = (position) => {
    updatePaint(currentIndex, (paint) => ({
      ...paint, sampleIds: paint.sampleIds.filter((_, i) => i !== position),
    }));
  };

  const movePick = (position, delta) => {
    updatePaint(currentIndex, (paint) => {
      const target = position + delta;
      if (target < 0 || target >= paint.sampleIds.length) return paint;
      const next = [...paint.sampleIds];
      [next[position], next[target]] = [next[target], next[position]];
      return { ...paint, sampleIds: next };
    });
  };

  const setExcluded = (index, excluded) => {
    updatePaint(index, (paint) => ({ ...paint, excluded }));
  };

  const changeColorMode = (mode) => {
    setColorMode(mode);
    setSelectedPaint(0);
    setErrorMessage(null);
  };

  // Next asks for confirmation, then stores the picks and fixes them for
  // good: the backend stamps the reproduction final and refuses every
  // later change to it.
  const finalizeGradient = async () => {
    setSaving(true);
    setErrorMessage(null);
    try {
      await axios.post("api/reproduction/update_reproduction", {
        reproduction_id: reproductionId,
        ...setupPayload(),
      });
      await axios.post("api/reproduction/finalize", { reproduction_id: reproductionId });
      setFinalizeDialogOpen(false);
      navigate(stepRoute("marking", "GRADIENT_REPRODUCTION"));
    } catch (error) {
      console.error("Error saving the gradient:", error);
      const detail = error?.response?.data?.error;
      setFinalizeDialogOpen(false);
      setErrorMessage(detail || "Saving the gradient failed.");
    } finally {
      setSaving(false);
    }
  };

  const paletteBySample = new Map(palette.map((entry) => [entry.sample_id, entry]));
  const gridColumns = paletteColumns || Math.max(1, Math.ceil(Math.sqrt(palette.length || 1)));
  const markedIds = markedSampleIds(effectivePaints);
  const nextDisabled = !setupComplete(effectivePaints);
  // Per-paint picks the "same" mode leaves out - kept on the page until the
  // mode is switched back, never stored.
  const setAsidePicks = multiPaint && !perPaint && paints.slice(1).some((paint) => paint.sampleIds.length > 0);
  // The sequence the preview shows for the current paint (an inheriting
  // paint shows the first paint's).
  const shownSampleIds = pickedSampleIds.length ? pickedSampleIds : effectiveSampleIds(paints, currentIndex);
  const paletteIndexOf = (sampleId) => {
    const entry = paletteBySample.get(sampleId);
    return entry ? entry.index : "?";
  };

  const swatchTitle = (entry) => (
    `L*=${Number(entry.l_star).toFixed(1)} a*=${Number(entry.a_star).toFixed(1)} `
    + `b*=${Number(entry.b_star).toFixed(1)} · width ${Number(entry.width_mm).toFixed(3)} mm`
  );

  const paintStatus = (paint, index) => {
    if (paint.excluded) return "Not marked";
    if (!perPaint) return "";
    if (paint.sampleIds.length) return `${paint.sampleIds.length} ${paint.sampleIds.length === 1 ? "color" : "colors"}`;
    if (index > 0 && paints[0].sampleIds.length) return `Follows ${paints[0].label}`;
    return "No colors yet";
  };
  const paintCaption = (paint, index) => [
    paint.componentCount === null ? "" : `${paint.componentCount} ${paint.componentCount === 1 ? "shape" : "shapes"}`,
    paintStatus(paint, index),
  ].filter(Boolean).join(" · ");

  const directionText = (GRADIENT_DIRECTIONS.find((option) => option.value === direction)?.label || direction).toLowerCase();
  const scopeText = (GRADIENT_SCOPES.find((option) => option.value === scope)?.label || scope).toLowerCase();
  const finalizeTail = "Colors, blend centers, direction and scope cannot be changed afterwards.";
  let finalizeText;
  if (perPaint) {
    finalizeText = `${paints.length} paints, ${markedIds.length} colors, ${directionText}, ${scopeText}. ${finalizeTail}`;
  } else if (multiPaint) {
    finalizeText = `${markedIds.length} colors for every paint, ${directionText}, ${scopeText}. ${finalizeTail}`;
  } else {
    finalizeText = `${markedIds.length} colors, ${directionText}, ${scopeText}. ${finalizeTail}`;
  }

  const scopeSentence = scope === "shape"
    ? `Every shape runs from the first color to the last on its own, ${axis.start} to ${axis.end}.`
    : `The blend runs from the first color to the last across the whole design, ${axis.start} to ${axis.end}.`;
  let modeSentence = "";
  if (perPaint) {
    modeSentence = " Each paint blends through its own colors; a paint without colors of its own follows the first paint.";
  } else if (multiPaint) {
    modeSentence = " Every paint of the shape blends through the same colors.";
  }

  const sequenceRun = shownSampleIds.length >= 2
    ? `Runs ${axis.arrow}: Color ${paletteIndexOf(shownSampleIds[0])} at the ${axis.start}, `
      + `Color ${paletteIndexOf(shownSampleIds[shownSampleIds.length - 1])} at the ${axis.end}.`
    : "";

  const EarlierIcon = axis.column ? ArrowUpwardIcon : ArrowBackIcon;
  const LaterIcon = axis.column ? ArrowDownwardIcon : ArrowForwardIcon;
  const endLabel = (position) => {
    if (pickedSampleIds.length < 2) return "";
    if (position === 0) return axis.start;
    if (position === pickedSampleIds.length - 1) return axis.end;
    return "";
  };

  return (
    <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
      <ExplorationPageShell
        title="Gradient"
        subtitle={multiPaint
          ? "Pick the palette colors the blend runs through, in order, and how the blends run: their direction, what one spans, and whether every paint of the shape shares the colors."
          : "Pick the palette colors the blend runs through, in order, and the direction of the gradient."}
        stepSidebar={gradientReproductionSidebar(1, markedIds.length || null)}
        footerBelowPaper={
          <Box sx={{ width: "100%" }}>
            <NavBarBottom
              variant="embedded"
              returnLabel="Back"
              prevPage={stepRoute("setup", "GRADIENT_REPRODUCTION")}
              nextPage={stepRoute("marking", "GRADIENT_REPRODUCTION")}
              enableNextPage={nextDisabled}
              nextLoading={saving}
              nextPageClickFunc={() => setFinalizeDialogOpen(true)}
            />
          </Box>
        }
      >
        <Stack spacing={2.5} sx={{ width: "100%" }}>
          {errorMessage && (
            <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gap: 2.5,
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
            }}
          >
            {/* Left: how the blends run, then the picks. */}
            <Stack spacing={2.5}>
              <Box>
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
                  <TextField
                    select
                    label="Direction"
                    value={direction}
                    onChange={(event) => setDirection(event.target.value)}
                    sx={{ minWidth: 220 }}
                    size="small"
                  >
                    {GRADIENT_DIRECTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Blend spans"
                    value={scope}
                    onChange={(event) => setScope(event.target.value)}
                    sx={{ minWidth: 220 }}
                    size="small"
                  >
                    {GRADIENT_SCOPES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                  {multiPaint && (
                    <TextField
                      select
                      label="Colors"
                      value={colorMode}
                      onChange={(event) => changeColorMode(event.target.value)}
                      sx={{ minWidth: 220 }}
                      size="small"
                    >
                      {GRADIENT_COLOR_MODES.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {scopeSentence}
                  {modeSentence}
                </Typography>
              </Box>

              {multiPaint && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    Source paints
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {perPaint
                      ? "Select a paint to pick its colors. A paint without colors of its own follows the first paint."
                      : "Every paint blends through the colors picked below. Tick Do not mark to leave a paint out."}
                    {setAsidePicks ? " Colors picked per paint are set aside and not stored." : ""}
                  </Typography>
                  <Stack spacing={0.75}>
                    {paints.map((paint, index) => {
                      const selected = perPaint && index === selectedPaint;
                      const body = (
                        <>
                          <PaintSwatch display={paint.display} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{paint.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {paintCaption(paint, index)}
                            </Typography>
                          </Box>
                        </>
                      );
                      return (
                        <Box
                          key={paint.id || paint.key}
                          data-testid={`paint-row-${paint.key}`}
                          sx={{
                            display: "flex", alignItems: "center", gap: 1,
                            border: "1px solid", borderRadius: 2, pl: 1, pr: 0.5, py: 0.5,
                            borderColor: selected ? "primary.main" : "divider",
                            bgcolor: selected ? "action.selected" : "background.paper",
                          }}
                        >
                          {perPaint ? (
                            <ButtonBase
                              aria-label={`Paint ${paint.label}`}
                              aria-pressed={selected}
                              onClick={() => { setSelectedPaint(index); setErrorMessage(null); }}
                              sx={{ flex: 1, justifyContent: "flex-start", gap: 1, borderRadius: 1, textAlign: "left" }}
                            >
                              {body}
                            </ButtonBase>
                          ) : (
                            <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                              {body}
                            </Box>
                          )}
                          <FormControlLabel
                            sx={{ mr: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={paint.excluded}
                                onChange={(event) => setExcluded(index, event.target.checked)}
                                inputProps={{ "aria-label": `Do not mark ${paint.label}` }}
                              />
                            }
                            label={<Typography variant="caption">Do not mark</Typography>}
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                  {perPaint ? `Palette colors for ${current.label}` : "Palette colors"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Click a color to append it to the gradient; click it again to remove it. The colors are laid out as marked on the palette plate.
                </Typography>
                {palette.length === 0 ? (
                  <Typography variant="body2" color="text.disabled">
                    {loaded ? "The palette selection has no colors." : "Loading the palette..."}
                  </Typography>
                ) : (
                  <Box
                    data-testid="palette-grid"
                    data-columns={gridColumns}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
                      gap: 0.5,
                      width: "100%",
                      maxWidth: gridColumns * 44,
                    }}
                  >
                    {palette.map((entry, index) => {
                      const position = pickedSampleIds.indexOf(entry.sample_id);
                      const picked = position >= 0;
                      const row = present(entry.row) ? entry.row : Math.floor(index / gridColumns);
                      const column = present(entry.column) ? entry.column : index % gridColumns;
                      return (
                        <Tooltip key={entry.sample_id} title={swatchTitle(entry)}>
                          <Box
                            role="button"
                            tabIndex={0}
                            aria-label={`Palette color ${entry.index}`}
                            aria-pressed={picked}
                            data-row={row}
                            data-column={column}
                            onClick={() => togglePick(entry.sample_id)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") togglePick(entry.sample_id);
                            }}
                            sx={{
                              gridRow: row + 1, gridColumn: column + 1,
                              width: "100%", aspectRatio: "1 / 1", minWidth: 0, borderRadius: 1,
                              bgcolor: entry.hex,
                              border: picked ? "2px solid" : "1px solid rgba(15, 23, 42, 0.2)",
                              borderColor: picked ? "primary.main" : "rgba(15, 23, 42, 0.2)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "pointer",
                              position: "relative",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                fontSize: gridColumns > 10 ? "0.625rem" : "0.75rem",
                                lineHeight: 1,
                                color: "#ffffff",
                                textShadow: "0 0 3px rgba(0,0,0,0.9)",
                              }}
                            >
                              {picked ? position + 1 : entry.index}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                  Picked colors show their position in the gradient; unpicked ones their palette index.
                </Typography>
              </Box>

              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Gradient sequence
                  </Typography>
                  <Chip size="small" label={axis.arrow} data-testid="sequence-axis" />
                </Stack>
                {pickedSampleIds.length === 0 ? (
                  <Typography variant="body2" color="text.disabled">
                    {perPaint && selectedPaint > 0 && paints[0].sampleIds.length
                      ? `No colors picked: ${current.label} follows ${paints[0].label}.`
                      : "No colors picked yet. A gradient needs at least two."}
                  </Typography>
                ) : (
                  <Stack
                    direction={axis.column ? "column" : "row"}
                    flexWrap={axis.column ? "nowrap" : "wrap"}
                    useFlexGap
                    spacing={1}
                    alignItems={axis.column ? "flex-start" : "center"}
                  >
                    {pickedSampleIds.map((sampleId, position) => {
                      const entry = paletteBySample.get(sampleId);
                      const index = entry ? entry.index : "?";
                      const end = endLabel(position);
                      return (
                        <Stack
                          key={`${sampleId}-${position}`}
                          direction="row"
                          alignItems="center"
                          spacing={0.25}
                          sx={{
                            border: "1px solid", borderColor: "divider", borderRadius: 2,
                            pl: 1, pr: 0.25, py: 0.25, bgcolor: "background.paper",
                          }}
                        >
                          <Box
                            sx={{
                              width: 18, height: 18, borderRadius: 0.5, mr: 0.75,
                              bgcolor: entry ? entry.hex : "#cccccc",
                              border: "1px solid rgba(15, 23, 42, 0.2)",
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {`${position + 1}. Color ${index}`}
                          </Typography>
                          {end ? (
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                              {end}
                            </Typography>
                          ) : null}
                          <IconButton
                            size="small"
                            aria-label={`Move color ${index} earlier`}
                            disabled={position === 0}
                            onClick={() => movePick(position, -1)}
                          >
                            <EarlierIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label={`Move color ${index} later`}
                            disabled={position === pickedSampleIds.length - 1}
                            onClick={() => movePick(position, 1)}
                          >
                            <LaterIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label={`Remove color ${index}`}
                            onClick={() => removePick(position)}
                          >
                            <CloseIcon fontSize="inherit" />
                          </IconButton>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </Box>

              {pickedSampleIds.length >= 2 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    Blend centers
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Where two neighbouring colors mix half and half. 50 % is a linear blend.
                  </Typography>
                  <Stack spacing={1} sx={{ maxWidth: 420 }}>
                    {blendCenters.map((center, position) => {
                      const fromEntry = paletteBySample.get(pickedSampleIds[position]);
                      const toEntry = paletteBySample.get(pickedSampleIds[position + 1]);
                      const fromIndex = fromEntry ? fromEntry.index : "?";
                      const toIndex = toEntry ? toEntry.index : "?";
                      const transitions = blendCenters.length;
                      const shapePosition = (position + center) / transitions;
                      return (
                        <Box key={pairKey(pickedSampleIds[position], pickedSampleIds[position + 1])}>
                          <Typography variant="body2">
                            {`Color ${fromIndex} → Color ${toIndex} — ${formatPercent(center)}`}
                            {transitions > 1 ? ` (at ${formatPercent(shapePosition)} from the ${axis.start})` : ""}
                          </Typography>
                          <Slider
                            size="small"
                            aria-label={`Blend center color ${fromIndex} to color ${toIndex}`}
                            value={Math.round(center * 100)}
                            onChange={(event, value) => setBlendCenter(position, value / 100)}
                            min={1}
                            max={99}
                            step={1}
                            marks={[{ value: 50 }]}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => `${value} %`}
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              <Typography variant="body2" color="text.secondary">
                The halftone cell is sized from the picked colors' cluster widths, so each color is
                laid down at the density it was measured at.
              </Typography>
            </Stack>

            {/* Right: source shape next to the blended preview. */}
            <Box
              sx={{
                display: "grid",
                gap: 2.5,
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                alignSelf: "start",
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Source shape
                </Typography>
                <Box sx={imagePanelSx}>
                  {sourceImageUrl ? (
                    <img
                      src={sourceImageUrl}
                      alt="Source shape"
                      style={{
                        position: "absolute", inset: 0, width: "100%", height: "100%",
                        objectFit: "contain", display: "block",
                      }}
                    />
                  ) : (
                    <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Typography variant="caption" color="text.disabled" sx={{ px: 1.5, textAlign: "center" }}>
                        The uploaded shape appears here.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Gradient preview
                  </Typography>
                  {previewLoading ? (
                    <CircularProgress size={14} thickness={5} aria-label="Rendering preview" />
                  ) : null}
                  {markedIds.length > 0 ? (
                    <Chip size="small" label={`${markedIds.length} colors`} />
                  ) : null}
                </Stack>
                <Box sx={imagePanelSx}>
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Gradient preview"
                      style={{
                        position: "absolute", inset: 0, width: "100%", height: "100%",
                        objectFit: "contain", display: "block",
                      }}
                    />
                  ) : (
                    <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Typography variant="caption" color="text.disabled" sx={{ px: 1.5, textAlign: "center" }}>
                        Pick colors to see the blended shape here.
                      </Typography>
                    </Box>
                  )}
                  {markedIds.length >= 2 ? (
                    // The gradient axis over the preview: first colour at the
                    // start, last colour at the arrow head.
                    <Box
                      aria-hidden
                      data-testid="direction-marker"
                      data-direction={direction}
                      sx={{
                        position: "absolute", pointerEvents: "none", color: "text.secondary",
                        display: "flex", alignItems: "center",
                        // Wide enough for the "bottom" label, so nothing is clipped at the edge.
                        ...(axis.column
                          ? { left: 2, top: 6, bottom: 6, width: 40, flexDirection: "column" }
                          : { left: 6, right: 6, bottom: 4, height: 20, flexDirection: "row" }),
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: "0.625rem", lineHeight: 1 }}>
                        {axis.start}
                      </Typography>
                      <Box
                        sx={{
                          flex: 1, bgcolor: "currentColor", opacity: 0.45,
                          ...(axis.column ? { width: "2px", my: 0.5 } : { height: "2px", mx: 0.5 }),
                        }}
                      />
                      <LaterIcon sx={{ fontSize: 14, ...(axis.column ? { mt: -0.75 } : { ml: -0.75 }) }} />
                      <Typography variant="caption" sx={{ fontSize: "0.625rem", lineHeight: 1 }}>
                        {axis.end}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                  {sequenceRun ? `${sequenceRun} ` : ""}
                  Smooth blend of the picked colors; the marked result is halftoned and appears on the
                  Marking step.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Stack>
      </ExplorationPageShell>

      <FinalizeDialog
        open={finalizeDialogOpen}
        onClose={() => setFinalizeDialogOpen(false)}
        onConfirm={finalizeGradient}
        confirming={saving}
        title="Finalize the gradient?"
        text={finalizeText}
      />
    </Stack>
  );
}
